import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useLanguage } from "../i18n/LanguageContext";
import { trackEvent } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const ContactForm = () => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    gdpr: false,
  });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error(t.contact.validation);
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error(t.contact.emailInvalid);
      return;
    }
    if (form.phone.trim().length < 5) {
      toast.error(t.contact.phoneRequired);
      return;
    }
    if (!form.service) {
      toast.error(t.contact.serviceRequired);
      return;
    }
    if (form.message.trim().length < 20) {
      toast.error(t.contact.messageShort);
      return;
    }
    if (!form.gdpr) {
      toast.error(t.contact.gdprRequired);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
        consent: form.gdpr,
        language,
      });
      toast.success(t.contact.success);
      trackEvent("form_submit", { language, service: form.service || "none" });
      setForm({ name: "", email: "", phone: "", service: "", message: "", gdpr: false });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0d2b52] py-20 lg:py-28" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:items-start lg:px-8">
        <div>
          <p className="section-label !text-[#2f9be8]" data-testid="contact-label">
            {t.contact.label}
          </p>
          <h2
            className="font-display mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl"
            data-testid="contact-title"
          >
            {t.contact.title}
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-[#cbd5e1] sm:text-lg" data-testid="contact-subtitle">
            {t.contact.subtitle}
          </p>
          <img
            src="/assets/uba-office-reception.jpg"
            alt="UBA COMPANY București"
            className="mt-10 max-h-[420px] w-full max-w-lg object-cover"
            data-testid="contact-logo"
            loading="lazy"
          />
        </div>

        <form
          onSubmit={handleSubmit}
          className="border border-white/10 bg-white p-8"
          data-testid="contact-form"
        >
          <div className="space-y-5">
            <div>
              <Label htmlFor="contact-name" className="font-display font-semibold text-[#0d2b52]">
                {t.contact.name} *
              </Label>
              <Input
                id="contact-name"
                value={form.name}
                onChange={update("name")}
                required
                className="mt-2 rounded-none border-[#cbd5e1] focus-visible:ring-[#1d6fd1]"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label htmlFor="contact-email" className="font-display font-semibold text-[#0d2b52]">
                {t.contact.email} *
              </Label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                className="mt-2 rounded-none border-[#cbd5e1] focus-visible:ring-[#1d6fd1]"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <Label htmlFor="contact-phone" className="font-display font-semibold text-[#0d2b52]">
                {t.contact.phone} *
              </Label>
              <Input
                id="contact-phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                required
                className="mt-2 rounded-none border-[#cbd5e1] focus-visible:ring-[#1d6fd1]"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label className="font-display font-semibold text-[#0d2b52]">
                {t.contact.service} *
              </Label>
              <Select
                value={form.service}
                onValueChange={(v) => setForm((prev) => ({ ...prev, service: v }))}
              >
                <SelectTrigger
                  className="mt-2 rounded-none border-[#cbd5e1] focus:ring-[#1d6fd1]"
                  data-testid="contact-service-select"
                >
                  <SelectValue placeholder={t.contact.service} />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {t.contact.serviceOptions.map((option, i) => (
                    <SelectItem key={i} value={option} data-testid={`contact-service-option-${i}`}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="contact-message" className="font-display font-semibold text-[#0d2b52]">
                {t.contact.message} *
              </Label>
              <Textarea
                id="contact-message"
                value={form.message}
                onChange={update("message")}
                required
                rows={5}
                placeholder={t.contact.messagePlaceholder}
                className="mt-2 rounded-none border-[#cbd5e1] focus-visible:ring-[#1d6fd1]"
                data-testid="contact-message-input"
              />
            </div>
            <div className="flex items-start gap-3">
              <input
                id="contact-gdpr"
                type="checkbox"
                checked={form.gdpr}
                onChange={(e) => setForm((prev) => ({ ...prev, gdpr: e.target.checked }))}
                className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#1d6fd1]"
                data-testid="contact-gdpr-checkbox"
              />
              <Label htmlFor="contact-gdpr" className="cursor-pointer text-sm font-normal leading-relaxed text-[#475569]">
                {t.contact.gdprConsent}{" "}
                <a
                  href="#gdpr"
                  className="font-semibold text-[#1d6fd1] underline underline-offset-2 hover:text-[#0d2b52]"
                  data-testid="contact-gdpr-link"
                >
                  {t.contact.gdprLink}
                </a>
                . *
              </Label>
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full rounded-none bg-[#1d6fd1] py-6 font-display text-base font-bold text-white hover:bg-[#0d2b52] disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {sending ? t.contact.sending : t.contact.submit}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};
